import { auCentime } from './vat';
import { treizieme } from './salaire13';
import { hours } from './utils';

/** Mois « AAAA-MM » d'une date suisse (jj.mm.aaaa) ou ISO. */
export function moisDe(d) {
  const s = String(d || '');
  if (s.includes('-')) return s.slice(0, 7);
  const p = s.split('.');
  if (p.length >= 3) return p[2].slice(0, 4) + '-' + p[1].padStart(2, '0');
  return '';
}

/**
 * Mois d'une période de la comptabilité, sans ceux qui ne sont pas encore
 * arrivés : un salaire de décembre n'est pas versé en octobre.
 *
 * @param {number} annee exercice
 * @param {'annee'|'s1'|'s2'} periode période affichée
 * @param {Date} [maintenant] date de référence, pour les essais
 * @returns {string[]} mois « AAAA-MM »
 */
export function moisDeLaPeriode(annee, periode, maintenant = new Date()) {
  const debut = periode === 's2' ? 7 : 1;
  const fin = periode === 's1' ? 6 : 12;
  const courant = maintenant.getFullYear() * 100 + maintenant.getMonth() + 1;
  const mois = [];
  for (let m = debut; m <= fin; m++) {
    if (annee * 100 + m > courant) break;
    mois.push(annee + '-' + String(m).padStart(2, '0'));
  }
  return mois;
}

/** Vrai si l'employé faisait partie de l'entreprise ce mois-là. */
function presentCeMois(emp, mois) {
  const embauche = moisDe(emp.dateEmbauche);
  const fin = moisDe(emp.dateFin);
  return (!embauche || embauche <= mois) && (!fin || fin >= mois);
}

/**
 * Salaire brut d'un employé pour un mois, selon la règle de la fiche de
 * salaire : les heures pointées au taux horaire, sinon le salaire mensuel,
 * plus le 13e le mois où il se verse.
 *
 * @param {object} emp fiche de l'employé
 * @param {object[]} chantiers chantiers, pour leurs pointages
 * @param {string} mois « AAAA-MM »
 * @param {object} soc société, pour le versement du 13e
 * @returns {number} le brut du mois
 */
export function brutDuMois(emp, chantiers, mois, soc) {
  if (!presentCeMois(emp, mois)) return 0;
  const pts = [
    ...(chantiers || []).flatMap((c) =>
      (c.pointages || []).filter((p) => p.empId === emp.id && moisDe(p.date) === mois),
    ),
    // Les pointages de chantier reviennent aussi sur la fiche : seuls ceux
    // hors chantier (atelier, dépôt) s'y ajoutent.
    ...(emp.badges || []).filter((b) => b.chId == null && moisDe(b.date) === mois),
  ];
  const totalH = pts.reduce(
    (s, p) => (p.type === 'absence' ? s : s + hours(p.entree, p.sortie, p.pause)),
    0,
  );
  const txH = +emp.txH || 0;
  const salMois = totalH > 0 && txH > 0 ? totalH * txH : +emp.salBase || 0;
  // La fiche de salaire est au centime : l'année est la somme des fiches, pas
  // celle des heures non arrondies (3 centimes d'écart sur 44 fiches).
  return auCentime(salMois + treizieme({ emp, soc, moisPaie: mois, mensuel: salMois }));
}

/**
 * Salaires bruts versés sur une période, employé par employé.
 *
 * <p>La comptabilité multipliait le salaire mensuel des employés par le
 * nombre de mois : un employé payé à l'heure y comptait pour zéro, et ses
 * heures sur les chantiers n'apparaissaient nulle part.
 *
 * @param {object} p paramètres
 * @param {object[]} p.employes fiches des employés
 * @param {object[]} p.chantiers chantiers, pour les pointages
 * @param {string[]} p.mois mois de la période
 * @param {object} p.soc société
 * @param {(emp: object) => boolean} [p.exclu] fiche à ne pas compter (le patron indépendant)
 * @returns {number} total brut de la période
 */
export function salairesDeLaPeriode({ employes, chantiers, mois, soc, exclu }) {
  return (employes || [])
    .filter((e) => !(exclu && exclu(e)))
    .reduce((s, e) => s + mois.reduce((t, m) => t + brutDuMois(e, chantiers, m, soc), 0), 0);
}

/**
 * Mois de l'exercice pour lesquels l'employé a une fiche de salaire : de son
 * embauche (ou janvier) au mois écoulé, ou à son départ.
 *
 * @param {object} e fiche de l'employé
 * @param {number} annee exercice affiché
 * @param {Date} [maintenant] date de référence, pour les essais
 * @returns {string[]} mois « AAAA-MM », le plus récent d'abord
 */
export function moisDesFiches(e, annee, maintenant = new Date()) {
  const ecoule = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1, 1);
  const dernier = ecoule.getFullYear() * 100 + ecoule.getMonth() + 1;
  const debut = moisDe(e.dateEmbauche);
  const fin = moisDe(e.dateDepart) || moisDe(e.dateFin);
  const mois = [];
  for (let m = 12; m >= 1; m--) {
    const cle = annee + '-' + String(m).padStart(2, '0');
    if (annee * 100 + m > dernier) continue;
    if (debut && cle < debut) continue;
    if (fin && cle > fin) continue;
    mois.push(cle);
  }
  return mois;
}
